/* eslint-disable no-console, no-useless-escape */
import { registerLocaleData, ViewportScroller } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {
  enableProdMode,
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
  provideZonelessChangeDetection,
} from '@angular/core';

import { environment } from '@environment';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlFr } from '@core/i18n/mat-paginator-intl-fr';
import { WINDOW_PROVIDERS, ImageService } from '@wizbii-utils/angular/core';
import { CookieService } from 'ngx-cookie-service';
import { bugsnagEsbuildErrorHandlerFactory, BugsnagGroupingRule } from '@wizbii-utils/angular/bugsnag';
import { RouterStateSerializer, withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { WizbiiRouterStateSerializer } from '@core/serializer/router.serializer';
import { AppBrowserViewportScroller } from '@core/scroller/app-browser-viewport-scroller';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { JwtInterceptor, JWT_SERVICE_TOKEN } from '@wizbii/jwt';
import { JwtService } from '@core/services/jwt/jwt.service';
import { ALGOLIA_CREDENTIALS_FETCHERS_TOKEN, CredentialsFetchers, Credentials } from '@wizbii-utils/angular/algolia';
import { AlgoliaCredentialsService } from '@webservices/algolia/algolia-credentials.service';
import { Observable } from 'rxjs';
import { MUST_BE_AUTHORIZED_GUARD_CONFIG, PageUnauthorizedModule } from '@wizbii-components/bo-angular-ui';
import { Store, provideStore, withNgxsNoopExecutionStrategy } from '@ngxs/store';
import { JwtState } from '@stores/jwt/jwt.state';

import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { withPreloading, withInMemoryScrolling, withRouterConfig, provideRouter } from '@angular/router';
import { QuicklinkStrategy } from 'ngx-quicklink';
import { ngxsConfig } from '@core/ngxs.config';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { featuresProviders } from '@features/features.providers';
import { routes } from '@features/features.routes';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { CoreComponent } from '@core/components/core/core.component';

const MESSAGE_GROUPS: BugsnagGroupingRule[] = [
  { regex: /(Loading chunk) [0-9]+ (failed)[^]*/, msg: '$1 $2' },
  { regex: /(Uncaught \(in promise\): e: {)[^]*(\"status\":[0-9]{1,3},\"statusText\":\"[^"]+\")[^]*$/, msg: '$1$2}' },
  { regex: /(\/profiles)\/[^/]+(.*):/, msg: '$1/<profile-id>$2:' },
  { regex: /(\/user)\/[^/]+(.*):/, msg: '$1/<user-id>$2:' },
  { regex: /(\/company)\/[^/]+(.*):/, msg: '$1/<company-id>$2:' },
  { regex: /(\/jobs)\/[^/]+(.*):/, msg: '$1/<job-id>$2:' },
  { regex: /(\/schools)\/[^/]+(.*):/, msg: '$1/<school-id>$2:' },
  { regex: /(\/location\?name=)[^:]+/, msg: '$1<keyword>' },
  { regex: /(\/location)\/[^:]+/, msg: '$1/<location-id>' },
  { regex: /(\/wizbii-files)\/[^:]+/, msg: '$1/<file-name>' },
  { regex: /(\/m-api.wizbii.com\/v1)\/[^:]+/, msg: '$1/<media-endpoint>' },
  { regex: /(animation trigger ".+" has failed to build)[^]*/, msg: '$1' },
  { regex: /(JSONP request)[^]*(failed|timed out)$/, msg: '$1 $2' },
];

registerLocaleData(localeFr, 'fr');

if (environment.isProdBuild) {
  enableProdMode();
}

bootstrapApplication(CoreComponent, {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      MatProgressBarModule,
      LoadingBarModule,
      LoadingBarRouterModule,
      LoadingBarHttpClientModule,
      PageUnauthorizedModule
    ),
    ...featuresProviders,
    provideStore(
      [JwtState],
      ngxsConfig,
      withNgxsNoopExecutionStrategy(),
      withNgxsRouterPlugin(),
      withNgxsReduxDevtoolsPlugin({ name: 'Wizbii Admin v3', maxAge: 30 })
    ),
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlFr },
    // Nécessaire à `CkEditorService`, qui injecte `WINDOW`.
    WINDOW_PROVIDERS,
    CookieService,
    {
      provide: ErrorHandler,
      useFactory: bugsnagEsbuildErrorHandlerFactory({
        apiKey: environment.bugsnagId,
        appVersion: environment.version,
        autoTrackSessions: false,
        releaseStage: environment.platform,
        isDeployed: environment.isDeployed,
        groupingRules: MESSAGE_GROUPS,
      }),
    },
    { provide: RouterStateSerializer, useClass: WizbiiRouterStateSerializer },
    { provide: ViewportScroller, useClass: AppBrowserViewportScroller },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: JWT_SERVICE_TOKEN, useClass: JwtService },
    {
      provide: LOCALE_ID,
      useValue: 'fr-FR',
    },
    { provide: ImageService, useValue: new ImageService(environment.api.imaginary) },
    {
      provide: ALGOLIA_CREDENTIALS_FETCHERS_TOKEN,
      useFactory: (algoliaCredentialsService: AlgoliaCredentialsService): CredentialsFetchers => {
        const credentialsObservableFromAccount = algoliaCredentialsService.getCredentialsFromAccount();
        return {
          members: {
            fetchCredentials(): Observable<Credentials> {
              return credentialsObservableFromAccount;
            },
          },
        };
      },
      deps: [AlgoliaCredentialsService],
    },
    {
      provide: MUST_BE_AUTHORIZED_GUARD_CONFIG,
      useFactory: (store: Store) => ({
        listRoles$: store.select(JwtState.roles),
        listAuthorizedRoles: ['ROLE_ADMIN'],
        platform: environment.platform,
      }),
      deps: [Store],
    },
    provideZonelessChangeDetection(),
    provideAnimations(),
    provideRouter(
      routes,
      withPreloading(QuicklinkStrategy),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload', paramsInheritanceStrategy: 'always' })
    ),
  ],
}).catch((err) => console.error(err));
